const ONE_DAY_MS = 1000 * 60 * 60 * 24;
const ONE_WEEK_MS = ONE_DAY_MS * 7;
const ONE_HOUR_MS = 1000 * 60 * 60;

export function isToday(date: Date): boolean {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

export function isDateBeforeToday(date: Date): boolean {
  return date ? new Date(date) <= new Date() : false;
}

export function sortByDate<T>(items: T[], dateProperty: keyof T): T[] {
  return items.sort(
    (item1, item2) => new Date(item2[dateProperty as string]).getTime() - new Date(item1[dateProperty as string]).getTime()
  );
}

export function isDateBetween(startDate: Date, endDate: Date, checkDate: Date, includeEndDate = false): boolean {
  return checkDate >= startDate && (includeEndDate ? checkDate <= endDate : checkDate < endDate);
}

export function formatDate(date: Date): string {
  const dateObj = new Date(date);
  return `${dateObj.getDate()}-${dateObj.getMonth() + 1}-${dateObj.getFullYear()}`;
}

/**
 * This method receives a date (Date or string format) and converts it into unix timestamp.
 * If this timestamp is not ISO formatted, we add Z to its end since we assume the passed
 * time timestamp is UTC.
 * @param date string or Date
 * @param isIso if this is string and not iso format, we will add timezone Z to its end
 * @return unix timestamp (without milliseconds)
 */
export function convertToUTCTimestamp(date: string | Date, isIso = false): number {
  let currDate: Date;
  if (typeof date === 'string') {
    // if we receive this time
    currDate = new Date(!isIso ? date + 'Z' : date);
  } else {
    currDate = date;
  }

  // adding this extra second since we get rid of the milliseconds
  currDate.setSeconds(currDate.getSeconds() + 1);
  // python datetime unix convention doesn't like milliseconds in their unix time
  // the solution is to divide by 1000 :S
  return Math.ceil(Date.parse(currDate.toUTCString()) / 1000);
}

/**
 *  This method receives dates and return the amount of days diff between them (how many days from the first date to the second date)
 * @param firstDate  |  usually future date
 * @param secondDate |  usually current date
 * @returns Number
 */
export function getDiffBetweenDatesInDays(firstDate: Date, secondDate: Date = new Date()): number {
  const differenceMs = firstDate.getTime() - secondDate.getTime();

  return Math.ceil(differenceMs / ONE_DAY_MS);
}

export function getDiffBetweenDatesInWeeks(firstDate: Date, secondDate: Date = new Date()): number {
  const differenceMs = firstDate.getTime() - secondDate.getTime();

  return parseFloat((differenceMs / ONE_WEEK_MS).toFixed(1));
}

export function convertToDateType(date: string | Date): Date {
  if (!date) {
    return undefined;
  }
  if (typeof date === 'string') {
    return new Date(Date.parse(date));
  }
  return date;
}

export function isDatesEqual(firstDate: Date, secondDate: Date = new Date()): boolean {
  return firstDate.setHours(0, 0, 0, 0) === secondDate.setHours(0, 0, 0, 0);
}

export function getTomorrowDate(): Date {
  const tomorrow = new Date();

  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return tomorrow;
}

export function isTomorrowDate(date: Date): boolean {
  const dateToCheck = date;

  dateToCheck.setHours(0, 0, 0, 0);

  return dateToCheck.toLocaleDateString() === getTomorrowDate().toLocaleDateString();
}

export function isPassedDate(dateToCheck: Date): boolean {
  return new Date(new Date().setHours(0, 0, 0, 0)) > new Date(new Date(dateToCheck.setHours(0, 0, 0, 0)));
}

export function hasXHoursPassed(dateString: string, howManyHours: number): boolean {
  const givenDate = new Date(dateString);
  const currentDate = new Date();
  const difference = currentDate.getTime() - givenDate.getTime();
  const differenceInHours = difference / (ONE_HOUR_MS);

  return differenceInHours > howManyHours;
}

export function addYearsToDate(date: string | Date, years: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + years * 12);
  return result;
}

export function subtractYearsFromDate(date: string | Date, years: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() - years * 12);
  return result;
}

export function subtractDaysFromDate(date: string | Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

export function addDaysToDate(date: string | Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function addMonthsToDate(monthsAmount: number, date: string | Date = new Date()): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + monthsAmount);

  return result;
}
